import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Reveal from "@/components/base/Reveal";
import usePageMeta from "@/hooks/usePageMeta";
import { articles, categories, siteInfo, trendingList } from "@/mocks/news";

const formatViews = (views: number) =>
  views >= 10000 ? `${(views / 10000).toFixed(1)} 万` : `${views}`;

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((item) => item.id === id);
  const category = categories.find((item) => item.slug === article?.categorySlug);

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = articles.filter(
      (item) => item.categorySlug === article.categorySlug && item.id !== article.id,
    );
    const others = articles.filter(
      (item) => item.categorySlug !== article.categorySlug && item.id !== article.id,
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  const jsonLd = useMemo(() => {
    if (!article) return undefined;
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        datePublished: article.date,
        dateModified: article.date,
        inLanguage: "zh-CN",
        articleSection: article.categoryName,
        keywords: article.tags.join(","),
        author: { "@type": "Organization", name: article.author },
        publisher: { "@type": "Organization", name: siteInfo.name },
        mainEntityOfPage: `https://sl858.cn/article/${article.id}`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://sl858.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: article.categoryName,
            item: `https://sl858.cn/category/${article.categorySlug}`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: article.title,
            item: `https://sl858.cn/article/${article.id}`,
          },
        ],
      },
    ];
  }, [article]);

  usePageMeta({
    title: article
      ? `${article.title} - 独家黑料速递｜料闻社`
      : "条目不存在 - 料闻社",
    description: article
      ? `${article.excerpt}（料闻社 ${article.categoryName} · ${article.date}）`
      : "该速递条目可能已被合并或调整，请返回全部速递页查看最新内容。",
    keywords: article
      ? `${article.tags.join(",")},独家黑料,独家黑料速递,${article.categoryName}`
      : "独家黑料,独家黑料速递",
    path: `/article/${id}`,
    type: "article",
    jsonLd,
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-24">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-2xl text-foreground-500" />
            </span>
            <h1 className="font-heading text-xl font-bold text-foreground-950">条目不存在</h1>
            <p className="max-w-md text-sm text-foreground-600">
              该速递条目可能已被合并或调整。可以回到全部速递页查看最新内容。
            </p>
            <Link
              to="/category/all"
              className="inline-flex h-11 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              返回全部速递
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-8 md:px-8 md:py-12">
        <Breadcrumb
          items={[
            { label: article.categoryName, to: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_300px] lg:gap-14">
          <article className="w-full min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${article.categorySlug}`}
                className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {article.categoryName}
              </Link>
              {article.hot && (
                <span className="flex items-center gap-1 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
                  <i className="ri-fire-line" />
                  高热条目
                </span>
              )}
              <span className="rounded-md border border-background-300 bg-background-100 px-2.5 py-1 text-xs text-foreground-600">
                {category ? category.name : "速递"}
              </span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>

            <p className="mt-4 border-l-2 border-primary-400 pl-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.excerpt}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3.5 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" />
                {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-timer-line" />
                约 {article.readTime} 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次阅读
              </span>
            </div>

            <div className="mt-6 h-64 w-full overflow-hidden rounded-lg md:h-[380px]">
              <img
                src={article.cover}
                alt={`${article.title} - 独家黑料速递配图`}
                title={`${article.title} · 料闻社速递`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((paragraph, index) => (
                <p
                  key={`para-${index}`}
                  className="text-base leading-[1.9] text-foreground-800"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-information-line text-accent-700" />
                </span>
                依据说明
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                本文按时间顺序整理公开可查的信息，并把官方口径、可核对记录与暂缺依据的线索分开标注。
                站内人物与事件均为虚构设定，用于演示资讯站的信息组织方式，不指向任何真实的个人或机构。
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/all?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  # {tag}
                </Link>
              ))}
            </div>
          </article>

          <aside className="w-full space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-4">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-fire-line text-primary-600" />
                </span>
                今日热榜
              </h2>
              <ul className="mt-2">
                {trendingList.slice(0, 5).map((item, index) => (
                  <li key={item.rank}>
                    <Link
                      to={`/article/${item.articleId}`}
                      className="group flex items-start gap-3 border-b border-background-200 py-3 last:border-b-0"
                    >
                      <span className="label-font mt-0.5 w-6 shrink-0 text-sm font-semibold text-primary-500">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100/70 p-4">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-folder-open-line text-primary-600" />
                </span>
                浏览其他栏目
              </h2>
              <ul className="mt-3 space-y-1">
                {categories.map((cat) => (
                  <li key={cat.slug}>
                    <Link
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200"
                    >
                      <i className={cat.icon} />
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-950 p-5">
              <h2 className="font-heading text-base font-bold text-background-50">
                有更完整的线索？
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                欢迎把可核对的时间与记录投递给我们，核实后会在原文追加更新，而不是删除原内容。
              </p>
              <Link
                to="/#subscribe"
                className="mt-4 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line" />
                投递线索
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14 border-t border-background-200 pt-10">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
                相关速递条目
              </h2>
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-sm text-primary-600 transition-colors hover:text-primary-700"
              >
                查看更多 {article.categoryName} →
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item, index) => (
                <Reveal key={item.id} delay={index * 60}>
                  <ArticleCard article={item} />
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}